import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import { motion, useAnimation } from "framer-motion";
import { Box, IconButton } from "@mui/material";
import VSNImage from "../../../shared/VSNImage";
import VSNTypography from "../../../shared/VSNTypography";
import whiteArrow from "../../../assets/quickView/svg/whiteArrow.svg";
import { UltrasoundOrgan } from "./CPETDashboard/AssessmentConstant";

const useStyles = makeStyles(() => ({
  card: {
    cursor: "pointer",
    border: "1px solid transparent",
    borderRadius: 12,
    background: "#F2E7F5",
    transition: "all 0.3s ease",
    height: 130,
    "& .paragraph": {
      display: "none",
      color: "#3A3A40",
      lineHeight: "23px",
    },
    "& img": {
      filter: "brightness(0)",
    },
  },
  activeCard: {
    width: 450,
    minHeight: 240,
    maxHeight: 240,
    borderColor: "#8B3F9E",
    overflowY: "auto",
    "& .paragraph": {
      display: "block !important",
      lineHeight: "20px",
    },
    "& img": {
      transform: "scale(1.3)",
      filter: "brightness(1)",
    },
  },
  slideArrowLeft: {
    "&.MuiButtonBase-root": {
      cursor: "pointer",
      background: `url(${whiteArrow})`,
      borderRadius: "50%",
      color: "#000",
      height: 24,
      width: 24,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "10px",
      transform: "rotate(0deg)",
      position: "absolute",
      left: -15,
      top: "50%",
      zIndex: 999,
    },
  },
  slideArrowRight: {
    "&.MuiButtonBase-root": {
      cursor: "pointer",
      background: `url(${whiteArrow})`,
      borderRadius: "50%",
      color: "#000",
      height: 24,
      width: 24,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "10px",
      transform: "rotate(180deg)",
      position: "absolute",
      right: -15,
      top: "50%",
      zIndex: 999,
    },
  },
}));

const CustomSlider = ({ data }) => {
  const classes = useStyles();
  const containerRef = useRef(null);
  const controls = useAnimation();
  const [activeSlide, setActiveSlide] = useState(0);
  const [direction, setDirection] = useState(null);

  const handleCardClick = (index) => {
    setActiveSlide(index);
    setDirection("right");
  };

  const moveCard = (direction) => {
    setActiveSlide((prev) => {
      setDirection(direction);
      if (direction === "left" && prev > 0) {
        return prev - 1;
      }
      if (direction === "right" && prev < data?.length - 1) {
        return prev + 1;
      }
      return prev;
    });
  };

  useEffect(() => {
    const updatePosition = (direction) => {
      const elements = document.querySelectorAll('[id^="journeyStep"]');
      if (elements[activeSlide]) {
        const containerOffset =
          containerRef.current.getBoundingClientRect().left;
        const elementOffset =
          elements[activeSlide].getBoundingClientRect().left;
        const activeCardWidth = 450;

        const transformPosition = elementOffset - containerOffset;

        controls.start({
          x:
            -transformPosition +
            (direction === "right" && activeCardWidth / 2 + 50),
        });
      }
    };

    updatePosition(direction);
    window.addEventListener("resize", updatePosition);

    return () => window.removeEventListener("resize", updatePosition);
  }, [activeSlide, controls]);

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: "#f8f1ff",
        borderRadius: "8px",
        p: 2,
      }}
    >
      <IconButton
        className={classes.slideArrowLeft}
        onClick={() => moveCard("left")}
      />
      <Box sx={{ overflow: "hidden" }}>
        <motion.div
          ref={containerRef}
          animate={controls}
          className="slider"
          style={{
            display: "flex",
            gap: "16px",
            cursor: "grab",
            alignItems: "flex-start",
          }}
          transition={{ type: "spring", stiffness: 100, damping: 30 }}
        >
          {data?.map(({ icon, name, description, db_key }, index) => {
            const matchOrgan = UltrasoundOrgan?.find(
              (item) => item.key === db_key
            );
            return (
              <motion.div
                key={index}
                id={`journeyStep${index}`}
                onClick={() => handleCardClick(index)}
                className={`${classes.card} ${
                  activeSlide === index ? classes.activeCard : ""
                }`}
                style={{
                  backgroundColor: "#F2E7F5",
                  borderRadius: "16px",
                  boxShadow: 3,
                  padding: "18px",
                  minWidth: activeSlide === index ? "400px" : "130px",
                  cursor: "pointer",
                  transition: "min-width 0.3s ease",
                }}
              >
                <Box key={index} className="card">
                  <VSNImage src={matchOrgan?.svgIcon ?? icon} alt="icon" />
                  <VSNTypography
                    variant="subtitle1"
                    component="h6"
                    color="#202024"
                    lineHeight="20px"
                    fontWeight={700}
                    my={1}
                  >
                    {name}
                  </VSNTypography>
                  <VSNTypography className="paragraph">
                    {description}
                  </VSNTypography>
                </Box>
              </motion.div>
            );
          })}
        </motion.div>
      </Box>

      <IconButton
        className={classes.slideArrowRight}
        onClick={() => moveCard("right")}
      />
    </Box>
  );
};

export default CustomSlider;
